'use client';

import { FC, ReactNode, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

type Props = { children: ReactNode; className?: string };

// Row of CTAs that keeps natural widths when both fit side-by-side. As soon
// as they wrap onto separate rows, both children get min-width'd to the
// widest one's natural width so the shorter one grows to match — CSS alone
// can't detect wrapping, so we watch container size and re-measure.
export const CtaEqualWidthRow: FC<Props> = ({ children, className = '' }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    let lastContainerWidth = -1;

    const sync = () => {
      const containerWidth = el.clientWidth;
      // Guard against loops: the min-width we apply below only affects child
      // height, not the container's width, so if the container width is
      // unchanged we can safely skip.
      if (containerWidth === lastContainerWidth) {
        return;
      }
      lastContainerWidth = containerWidth;

      const items = Array.from(el.children) as HTMLElement[];
      if (items.length < 2) {
        return;
      }

      // Clear so both natural widths and the natural wrap state are readable.
      items.forEach((i) => {
        i.style.minWidth = '';
      });

      const naturalMax = Math.max(
        ...items.map((i) => i.getBoundingClientRect().width),
      );
      const firstTop = items[0].offsetTop;
      const wrapped = items.some((i) => i.offsetTop !== firstTop);

      if (wrapped) {
        const px = `${Math.ceil(naturalMax)}px`;
        items.forEach((i) => {
          i.style.minWidth = px;
        });
      }
    };

    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn('flex flex-wrap items-center gap-6', className)}
    >
      {children}
    </div>
  );
};
